import { useQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { EventTimeline, SpecialProperty, TimelineEvent } from "@/components/timeline";
import { AssistantActivitySummarySchema } from "@/domains/openapi/types";
import { api } from "@/modules/api";

interface AssistantActivityTimelineProps {
  sessionId: string;
}

const coerceNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const formatDuration = (durationMs: number): string => {
  if (durationMs < 1000) {
    return `${Math.round(durationMs)}ms`;
  }
  const totalSeconds = Math.round(durationMs / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

const pluralize = (count: number, singular: string, plural: string) =>
  count === 1 ? singular : plural;

const truncate = (value: string, maxLength: number) =>
  value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;

const buildRunSummary = (
  payload: Record<string, unknown> | null | undefined
): string | undefined => {
  if (!payload) {
    return undefined;
  }

  const parts: string[] = [];
  const durationMs = coerceNumber(payload.duration_ms);
  if (typeof durationMs === "number") {
    parts.push(`duration ${formatDuration(durationMs)}`);
  }

  const turnCount = coerceNumber(payload.turn_count);
  if (typeof turnCount === "number") {
    parts.push(`${turnCount} ${pluralize(turnCount, "turn", "turns")}`);
  }

  const toolCallCount = coerceNumber(payload.tool_call_count);
  if (typeof toolCallCount === "number") {
    parts.push(`${toolCallCount} ${pluralize(toolCallCount, "tool call", "tool calls")}`);
  }

  const threadsCreated = coerceNumber(payload.threads_created);
  if (typeof threadsCreated === "number" && threadsCreated > 0) {
    parts.push(`${threadsCreated} ${pluralize(threadsCreated, "thread", "threads")} created`);
  }

  const threadsModified = coerceNumber(payload.threads_modified);
  if (typeof threadsModified === "number" && threadsModified > 0) {
    parts.push(`${threadsModified} ${pluralize(threadsModified, "thread", "threads")} modified`);
  }

  const tasksCreated = coerceNumber(payload.tasks_created);
  if (typeof tasksCreated === "number" && tasksCreated > 0) {
    parts.push(`${tasksCreated} ${pluralize(tasksCreated, "task", "tasks")} created`);
  }

  const tasksModified = coerceNumber(payload.tasks_modified);
  if (typeof tasksModified === "number" && tasksModified > 0) {
    parts.push(`${tasksModified} ${pluralize(tasksModified, "task", "tasks")} modified`);
  }

  const remindersCreated = coerceNumber(payload.reminders_created);
  if (typeof remindersCreated === "number" && remindersCreated > 0) {
    parts.push(
      `${remindersCreated} ${pluralize(remindersCreated, "reminder", "reminders")} created`
    );
  }

  const remindersModified = coerceNumber(payload.reminders_modified);
  if (typeof remindersModified === "number" && remindersModified > 0) {
    parts.push(
      `${remindersModified} ${pluralize(remindersModified, "reminder", "reminders")} modified`
    );
  }

  return parts.length ? parts.join(" | ") : undefined;
};

const buildLogfireUrlForRun = (assistantRunId: string): string => {
  const query = encodeURIComponent(
    `attributes->>'assistant_run_id' = '${assistantRunId}'`
  ).replaceAll("%20", "+");
  return `https://logfire-us.pydantic.dev/mem/backend?q=${query}&last=%226h%22`;
};

const buildTriggerSummary = (
  payload: Record<string, unknown> | null | undefined
): string | undefined => {
  if (!payload) {
    return undefined;
  }

  const notificationKind =
    typeof payload.notification_kind === "string" ? payload.notification_kind : undefined;
  if (!notificationKind) {
    return undefined;
  }

  const parts: string[] = [notificationKind];

  switch (notificationKind) {
    case "USER_MESSAGE": {
      if (typeof payload.source === "string") {
        parts.push(payload.source);
      }
      if (typeof payload.text === "string" && payload.text.trim() !== "") {
        parts.push(`"${truncate(payload.text.trim(), 80)}"`);
      }
      break;
    }
    case "NOTE_EDIT_WINDOW_CREATED": {
      const editCount = coerceNumber(payload.edit_count);
      if (typeof editCount === "number") {
        parts.push(`${editCount} edits`);
      }
      if (typeof payload.note_id === "string") {
        parts.push(`note ${payload.note_id}`);
      }
      break;
    }
    case "NOTES_REVIEW_REQUESTED": {
      const noteCount = coerceNumber(payload.note_count);
      if (typeof noteCount === "number") {
        parts.push(`${noteCount} notes`);
      }
      if (typeof payload.timezone === "string") {
        parts.push(payload.timezone);
      }
      break;
    }
    case "ASSISTANT_OBJECTIVE_DUE":
    case "ASSISTANT_SELF_REVIEW_DUE": {
      if (typeof payload.reason === "string") {
        parts.push(truncate(payload.reason, 120));
      }
      break;
    }
    case "SCHEDULED_REMINDER_DUE": {
      if (typeof payload.content === "string") {
        parts.push(truncate(payload.content, 120));
      }
      break;
    }
    case "MANUAL_TRIGGER": {
      if (typeof payload.source === "string") {
        parts.push(payload.source);
      }
      if (typeof payload.reason === "string") {
        parts.push(payload.reason);
      }
      break;
    }
    case "SLACK_REACTION_ADDED": {
      if (typeof payload.emoji === "string") {
        parts.push(`:${payload.emoji}:`);
      }
      break;
    }
    default:
      break;
  }

  return parts.join(" | ");
};

const resolveRunSummary = (
  payload: Record<string, unknown> | null | undefined
): string | undefined => {
  if (!payload) {
    return undefined;
  }

  const explicitSummary = typeof payload.summary === "string" ? payload.summary.trim() : undefined;
  if (explicitSummary) {
    return explicitSummary;
  }

  return buildRunSummary(payload);
};

const resolveEventKind = (activity: AssistantActivitySummarySchema): string => {
  if (activity.kind === "run") {
    const status =
      typeof activity.payload?.status === "string" ? activity.payload.status : undefined;
    if (status === "success") {
      return "run_complete";
    }
    if (status === "error") {
      return "run_fail";
    }
    if (status === "canceled") {
      return "run_canceled";
    }
    return "run";
  }

  if (activity.kind === "trigger") {
    const triggerKind =
      typeof activity.payload?.trigger_kind === "string"
        ? activity.payload.trigger_kind
        : undefined;
    const notificationKind =
      typeof activity.payload?.notification_kind === "string"
        ? activity.payload.notification_kind
        : undefined;
    if (notificationKind) {
      return `trigger_created_${notificationKind.toLowerCase()}`;
    }
    if (triggerKind) {
      return `trigger_created_${triggerKind.toLowerCase()}`;
    }
    return "trigger_created";
  }

  return activity.kind;
};

const buildTimelineEvents = (activities: AssistantActivitySummarySchema[]): TimelineEvent[] => {
  return activities.map(activity => {
    const properties: Record<string, unknown> = {
      ...(activity.payload || {}),
    };

    if (activity.assistant_run_id) {
      properties.assistant_run_id = activity.assistant_run_id;
    }

    if (activity.assistant_trigger_id) {
      properties.assistant_trigger_id = activity.assistant_trigger_id;
    }

    return {
      id: activity.id,
      kind: resolveEventKind(activity),
      occurred_at: activity.occurred_at,
      properties_json: properties,
      summary:
        activity.kind === "run"
          ? resolveRunSummary(activity.payload || {})
          : buildTriggerSummary(activity.payload || {}),
    };
  });
};

const getSpecialProperties = (properties: Record<string, unknown>): SpecialProperty[] => {
  const special: SpecialProperty[] = [];

  if (typeof properties.assistant_run_id === "string") {
    special.push({
      key: "assistant_run_id",
      label: "Run Events",
      id: properties.assistant_run_id,
      route: atlasRoutes.assistantRunsEvents({
        params: { assistantRunId: properties.assistant_run_id },
      }).path,
    });
    special.push({
      key: "assistant_run_logfire",
      label: "Logfire",
      id: properties.assistant_run_id,
      href: buildLogfireUrlForRun(properties.assistant_run_id),
      external: true,
    });
  }

  return special;
};

export const AssistantActivityTimeline = ({ sessionId }: AssistantActivityTimelineProps) => {
  const query = useQuery({
    queryKey: ["assistant-activities", sessionId],
    queryFn: () =>
      api.post("/internal/v1/assistant-activities/search", {
        body: {
          session_id: sessionId,
          limit: 200,
          offset: 0,
        },
      }),
    enabled: Boolean(sessionId),
    staleTime: 30 * 1000,
  });

  const activities = query.data?.data?.results ?? [];
  const total = query.data?.data?.total ?? 0;

  return (
    <EventTimeline<TimelineEvent>
      events={buildTimelineEvents(activities)}
      total={total}
      isLoading={query.isLoading}
      isError={query.isError}
      emptyMessage="No assistant activity recorded yet."
      getSpecialProperties={getSpecialProperties}
      sortDirection="desc"
      title="Timeline"
    />
  );
};
